import type {DueDateResult} from '@vat/engine/deadlines';
import {VAT_DEADLINE_COPY} from '../../vat-copy';

/**
 * Fristen-Darstellung (Spec 9): „fällig 11.05.2026 (Werktag, Dauerfrist)“, rot ab drei Tage vor der Frist,
 * „überfällig“ danach; BEFREIT ohne Fristen. Reines TypeScript – „heute“ steckt bereits im DueDateResult.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export type VatDeadlineTone = 'ok' | 'soon' | 'overdue' | 'none';

export const SOON_DAYS = 3;

export const formatDateDE = (date: Date | null | undefined): string =>
    date instanceof Date && !isNaN(date.getTime())
        ? `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`
        : '–';

const sameDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const isShiftedToBusinessDay = (due: DueDateResult): boolean => !sameDay(due.dueDate, due.dueDateEffective);

export const deadlineTone = (due: DueDateResult | null): VatDeadlineTone => {
    if (!due) {
        return 'none';
    }
    if (due.istUeberfaellig) {
        return 'overdue';
    }
    return due.tageBisFrist <= SOON_DAYS ? 'soon' : 'ok';
};

export const formatDueDateLabel = (due: DueDateResult | null, dauerfrist: boolean): string => {
    if (!due) {
        return VAT_DEADLINE_COPY.befreit;
    }
    const qualifiers: string[] = [];
    if (isShiftedToBusinessDay(due)) {
        qualifiers.push(VAT_DEADLINE_COPY.workday);
    }
    if (dauerfrist) {
        qualifiers.push(VAT_DEADLINE_COPY.dauerfrist);
    }
    let label = VAT_DEADLINE_COPY.due(formatDateDE(due.dueDateEffective));
    if (qualifiers.length) {
        label += ` (${qualifiers.join(', ')})`;
    }
    if (due.istUeberfaellig) {
        return `${label} · ${VAT_DEADLINE_COPY.overdue}`;
    }
    if (due.tageBisFrist === 0) {
        return `${label} · ${VAT_DEADLINE_COPY.today}`;
    }
    if (due.tageBisFrist <= SOON_DAYS) {
        return `${label} · ${VAT_DEADLINE_COPY.daysLeft(due.tageBisFrist)}`;
    }
    return label;
};

export const DEADLINE_TEXT_CLASSES: Record<VatDeadlineTone, string> = {
    ok: 'text-secondary',
    soon: 'text-red-600 dark:text-red-400 font-semibold',
    overdue: 'text-red-600 dark:text-red-400 font-semibold',
    none: 'text-secondary',
};

export const DEADLINE_CHIP_CLASSES: Record<VatDeadlineTone, string> = {
    ok: 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300',
    soon: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
    overdue: 'bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-300',
    none: 'bg-gray-100 text-gray-500 dark:bg-gray-500/20 dark:text-gray-400',
};
